import React from "react"
import { isNetworkRequestInFlight } from "@apollo/client/core/networkStatus"
import {
  type EntityLocation,
  ObjectKind,
  type SearchEntitiesQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import { defined, uniqueBy } from "@digits-shared/helpers/filters"
import { type LocationTypeaheadResult } from "./sharedTypeahead"
import { useTypeaheadSearchQuery } from "./useTypeaheadSearchQuery"

export function useTypeaheadLocations(text: string, kind: ObjectKind) {
  const { result: locationQueryResult, networkStatus: locationNetworkStatus } =
    useTypeaheadSearchQuery(text, ObjectKind.Location)

  return React.useMemo(
    () => ({
      loading: isNetworkRequestInFlight(locationNetworkStatus),
      results: buildLocationResults(locationQueryResult, text),
    }),
    [locationQueryResult, locationNetworkStatus, text]
  )
}

function locationToTypeaheadResult(location: EntityLocation): LocationTypeaheadResult {
  return {
    id: location.id,
    title: location.name,
    searchValues: [location.name].filter(defined),
    kind: ObjectKind.Location,
    entity: location,
    isSelectable: true,
  }
}

function extractLocationsFromQuery(queryResult: SearchEntitiesQuery | undefined): EntityLocation[] {
  if (!queryResult?.search) return []

  const { results, entities } = queryResult.search
  return results
    .map((result) => entities.locations?.find((location) => location.id === result.objectId.id))
    .filter(defined)
}

export function buildLocationResults(
  locationQueryResult: SearchEntitiesQuery | undefined,
  text: string
) {
  const locations = extractLocationsFromQuery(locationQueryResult)

  const results = [...locations.map((d) => locationToTypeaheadResult(d))]

  return results
    .filter(uniqueBy((d) => d.id))
    .filter(
      (d) => d.title !== "" && d.searchValues.join(" ").toLowerCase().includes(text.toLowerCase())
    )
    .sort((a, b) => a.title.localeCompare(b.title))
}
